<template>
  <component :is="el" :label="label" :align="align" :prop="prop" :required="required" :error="error">

    <!-- XOL-67 (B3): only with `error-id`, the error message carries that id (Element's own slot, same markup as its default)
         so the input can point `aria-describedby` at it. Without it Element renders its default message, unchanged. -->
    <template v-if="errorId" #error="slotProps">
      <div class="el-form-item__error" :id="errorId">{{ slotProps.error }}</div>
    </template>

    <span v-if="noForm && showLabel">{{label}}</span>

    <el-input
      v-if="!disabled"

      :type="type"
      :align="alignInput"
      :size="size"

      v-model="formData"

      :disabled="disabled"
      :pattern="pattern"
      :maxlength="maxlength"
      :show-word-limit="!!maxlength"

      v-bind="inputA11y"
      :validate-event="validateEvent"

      :autosize="type === 'textarea' ? {minRows: 2, maxRows: rows} : null"

      :step="type === 'number' ? (decimals > 0 ? stepValue : '1') : 'any'"

      :rows="rows"

      :placeholder="placeholder ? placeholder : label"
      @focus="$emit('focus')"
      @blur="$emit('blur')"
      @change="updated">

       <!-- eslint-disable-next-line vue/no-v-html -- developer-supplied prefix string from parent; not user-content -->
       <template v-if="prefix" #prepend><i v-if="isPrefixIcon" :class="prefix"></i><span v-else v-html="prefix"></span></template>
       <!-- eslint-disable-next-line vue/no-v-html -- developer-supplied suffix string from parent; not user-content -->
       <template v-if="suffix" #append><i v-if="isSuffixIcon" :class="suffix"></i><span v-else v-html="suffix"></span></template>
       <template #append><el-button v-if="appendIconButton" :on-icon-click="onButtonClick" :icon="appendIconButton"></el-button></template>

    </el-input>

    <span v-else>
      <!-- eslint-disable-next-line vue/no-v-html -- developer-supplied prefix string from parent; not user-content -->
      <span v-if="prefix"><i v-if="isPrefixIcon" :class="prefix"></i><span v-else v-html="prefix"></span></span>
      <b v-if="type === 'number'">{{this.formatNumber(this.formData, this.decimals)}}</b>
      <b v-else v-safe-html="formData" />
      <!-- eslint-disable-next-line vue/no-v-html -- developer-supplied suffix string from parent; not user-content -->
      <span v-if="suffix"><i v-if="isSuffixIcon" :class="suffix"></i><span v-else v-html="suffix"></span></span>
    </span><!-- XOL-596: optional trailing slot INSIDE the form item (the request drawer's "changed underneath you" cue); nothing renders when unused. No whitespace before it, so the unused markup is unchanged. --><slot name="cue"></slot>

  </component>
</template>

<script>

import NumberMixins from '@/mixins/NumberMixins'

export default {
  name: 'text-input',
  mixins: [NumberMixins],

  props: {
    label: {
      type: String,
      default: ''
    },

    showLabel: {
      type: Boolean,
      default: true
    },

    value: {
      required: true
    },

    type: {
      type: String,
      default: 'text'
    },

    size: {
      type: String,
      default: 'default'
    },

    pattern: {
      type: String,
      default: null
    },

    decimals: {
      type: Number,
      default: null
    },

    disabled: {
      type: Boolean,
      default: false
    },

    required: {
      type: Boolean,
      default: false
    },

    prefix: {
      type: String,
      default: null
    },

    rows: {
      type: Number,
      default: 4
    },

    suffix: {
      type: String,
      default: null
    },

    isPrefixIcon: {
      type: Boolean,
      default: false
    },

    isSuffixIcon: {
      type: Boolean,
      default: false
    },

    appendIconButton: {
      type: String,
      default: null
    },

    onButtonClick: {
      type: Function,
      default: null
    },

    placeholder: {
      type: String,
      default: null
    },

    align: {
      type: String,
      default: null
    },

    alignInput: {
      type: String,
      default: 'left'
    },

    helpText: {
      type: String,
      default: null
    },

    prop: {
      type: String,
      default: null
    },

    noForm: {
      type: Boolean,
      default: false
    },

    // XOL-487: additive — el-form-item's plain `error` String prop, a display
    // watcher independent of :model/:rules/validate(). default: null (not
    // '') so Vue omits the attribute entirely when `el` renders as a bare
    // div in no-form mode. Inert for every other call site.
    error: {
      type: String,
      default: null
    },

    // XOL-67 (B3): additive. The id of the error message, for `aria-describedby`. When set, a
    // non-empty `error` also marks the input `aria-invalid="true"`. Inert (no attribute, no custom
    // message markup) when not given, so every other call site renders as before.
    errorId: {
      type: String,
      default: null
    },

    // XOL-67 (B3): additive. Passed to the `el-input`: false stops its blur / change from validating the
    // surrounding form item. A form that does its own validation and shows its own `error` needs it, because
    // a blur validation (`prop` + rules, or just `required`) otherwise replaces the `error` shown with
    // "success" the moment the field loses focus. Default true: every other call site validates as before.
    validateEvent: {
      type: Boolean,
      default: true
    },

    // XOL-67 (B10): additive. The most characters the field takes; with it the el-input stops typing there and shows its counter.
    // Default null: no limit and no counter, every other call site renders as before.
    maxlength: {
      type: Number,
      default: null
    },

    // XOL-67 (V2): additive. The accessible name of the inner input (an attribute on a wrapper such as float-input / integer-input reaches here as this prop).
    // Default null: no attribute, every other call site renders as before.
    ariaLabel: {
      type: String,
      default: null
    },

    // XOL-67 (V3): additive. The id(s) of a note the input is described by (a hint under it), for `aria-describedby`. Merged with `errorId` when the field has an
    // error, in that order. Default null: no attribute, every other call site renders as before.
    describedBy: {
      type: String,
      default: null
    }
  },



  methods: {
    updated (value) {
      this.$emit('input', value)
      this.$emit('change', value)
    }

  },


  computed: {
    // Element's el-input passes $attrs to the native <input>.
    inputA11y: function () {
      var out = {}

      // el-input words its native input's aria-label from its own `label` prop (and writes it after any attribute), so the name goes in as that.
      if (this.ariaLabel) {
        out.label = this.ariaLabel
      }

      var ids = []

      if (this.describedBy) {
        ids.push(this.describedBy)
      }

      if (this.errorId && this.error) {
        out['aria-invalid'] = 'true'
        ids.push(this.errorId)
      }

      if (ids.length) {
        out['aria-describedby'] = ids.join(' ')
      }

      return out
    },

    stepValue: function () {
      if (this.decimals) {

        let s = '0.'
        for (let i = 1; i < this.decimals; i++) {
          s += '0'
        }

        s += '1'

        return Number(s)

      } else {
        return this.step
      }
    }
  },


  watch: {
    value: function (val) {
      this.formData = val
    }
  },


  data () {
    return {
      el: this.noForm ? 'div' : 'el-form-item',
      formData: this.value
    }
  }

}
</script>

<style scoped>
.el-input {
  width: 100%
}
</style>
